import React, { useEffect, useRef } from 'react';
import { useT } from '../../i18n/LanguageContext';
export default function ResourceDialog({
  title,
  onClose,
  children,
  wide = false,
  media = false,
  busy = false,
  returnFocus,
}) {
  const t = useT();
  const ref = useRef();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prior = returnFocus || document.activeElement;
    const overflow = document.body.style.overflow;
    const hidden = [];
    let branch = ref.current.parentElement;
    while (branch?.parentElement) {
      for (const node of branch.parentElement.children) {
        if (node === branch) continue;
        hidden.push({
          node,
          inert: node.hasAttribute('inert'),
          aria: node.getAttribute('aria-hidden'),
        });
        node.setAttribute('inert', '');
        node.setAttribute('aria-hidden', 'true');
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    document.body.style.overflow = 'hidden';
    ref.current.querySelector('button')?.focus();
    return () => {
      document.body.style.overflow = overflow;
      hidden.forEach(({ node, inert, aria }) => {
        if (!inert) node.removeAttribute('inert');
        if (aria === null) node.removeAttribute('aria-hidden');
        else node.setAttribute('aria-hidden', aria);
      });
      requestAnimationFrame(() => {
        if (prior?.isConnected) prior.focus();
        else document.querySelector('.rc-tabs [aria-selected="true"]')?.focus();
      });
    };
  }, [returnFocus]);
  const key = (e) => {
    if (e.key === 'Escape' && !busy) {
      e.preventDefault();
      close.current();
    }
    if (e.key !== 'Tab') return;
    const nodes = [
      ...ref.current.querySelectorAll(
        'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],iframe'
      ),
    ].filter((node) => !node.closest('[hidden],[inert]'));
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };
  return (
    <div className="rc-backdrop">
      <section
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={key}
        className={`rc-dialog${wide ? ' rc-dialog-wide' : ''}${media ? ' rc-dialog-media' : ''}`}
      >
        <header className="rc-dialog-header">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label={t('Close')}>
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
export function ResourceMedia({ url, mimeType, title }) {
  const t = useT();
  const [zoom, setZoom] = React.useState('fit');
  React.useEffect(() => {
    setZoom('fit');
  }, [url]);
  return mimeType === 'application/pdf' ? (
    <iframe className="rc-pdf" title={title} src={`${url}#view=Fit&navpanes=0`} />
  ) : (
    <div className="rc-image-preview">
      <label className="rc-zoom">
        {t('Zoom')}{' '}
        <select value={zoom} onChange={(e) => setZoom(e.target.value)}>
          <option value="fit">{t('Fit entire image')}</option>
          <option value="100">100%</option>
          <option value="150">150%</option>
          <option value="200">200%</option>
        </select>
      </label>
      <div className={`rc-image-scroll${zoom === 'fit' ? ' rc-image-fit' : ''}`}>
        <img
          style={zoom === 'fit' ? undefined : { width: `${zoom}%`, maxWidth: 'none' }}
          src={url}
          alt={title}
        />
      </div>
    </div>
  );
}
